import { Flag, HybridTooltip, Icon } from '@advanza/ui'
import { ReplacementLeadStats } from 'interfaces/ReplacementLeads'
import ReclaimStatusBar from './ReclaimStatusBar'

interface Props {
    stats: ReplacementLeadStats
}

export const WEEK_LIMIT_INFO =
    "Approved reclaims turn into replacement leads, delivered on top of the week limit. That's why " +
    'the weekly total can go above the limit.'

interface WeekLimitProps {
    /** Null when no week limit applies to this row, 0 when it is unlimited. */
    limit: number | null
    given: number
    withInfo?: boolean
}

/**
 * The limit this row is measured against, plus the replacement leads that are delivered on top of
 * it. Deliberately not deducted from anything: the two numbers are read together as "6 plus 4 more".
 */
const WeekLimit = ({ limit, given, withInfo = false }: WeekLimitProps) => {
    if (limit === null) {
        return <span className="text-grey-700">N/A</span>
    }

    return (
        <span className="inline-flex items-center gap-1">
            <span className="font-bold text-dark">{limit > 0 ? limit : '∞'}</span>
            {given > 0 && <span className="font-bold text-secondary-600">+{given}</span>}
            {withInfo && (
                <HybridTooltip
                    className="z-[101]"
                    childrenContent={<div className="max-w-[240px]">{WEEK_LIMIT_INFO}</div>}>
                    <span className="leading-[0]">
                        <Icon
                            name="info"
                            fontSize={14}
                            classNameIcon="!text-grey-700 cursor-help"
                        />
                    </span>
                </HybridTooltip>
            )}
        </span>
    )
}

/**
 * What came in this week against what the week allows. The combined row sits on top because it is
 * what CS needs most of the time; the per-subscription rows are only meaningful once the provider
 * has per-service limits, which is why their week limit shows N/A otherwise.
 */
const ReplacementLeadsThisWeek = ({ stats }: Props) => (
    <div className="flex flex-col gap-3">
        <div>
            <div className="flex items-center py-2 text-typography-text-xs font-bold text-grey-700 uppercase">
                <span className="flex-1">Filter</span>
                <span className="w-20 text-center">Received</span>
                <span className="w-28 text-center">Week limit</span>
            </div>
            <div className="flex items-center py-2.5 border-t border-grey-200">
                <span className="flex-1 font-bold text-dark">All subscriptions</span>
                <span className="w-20 text-center font-bold text-dark">
                    {stats.totals.weekReceived}
                </span>
                <span className="w-28 text-center">
                    <WeekLimit limit={stats.weekLimitTotal} given={stats.totals.given} withInfo />
                </span>
            </div>
            {stats.subscriptions.map((subscription) => (
                <div
                    key={subscription.subscriptionId}
                    className="flex items-center py-2.5 border-t border-grey-200">
                    <div className="flex-1 flex items-center gap-2">
                        <Flag countryCode={subscription.countryCode} />
                        <span className="font-bold text-dark">{subscription.serviceName}</span>
                    </div>
                    <span className="w-20 text-center text-bodyDark">
                        {subscription.weekReceived}
                    </span>
                    <span className="w-28 text-center">
                        <WeekLimit limit={subscription.maxWeek} given={subscription.given} />
                    </span>
                </div>
            ))}
        </div>
        <ReclaimStatusBar title="Reclaims this week" counts={stats.reclaims.thisWeek} />
    </div>
)

export default ReplacementLeadsThisWeek
